import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getTerminals, createTerminal, getMerchants } from '../services/adminService';
import CreateTerminalForm from '../components/CreateTerminalForm';
import * as XLSX from 'xlsx';
import {
    Alert,
    Box,
    Button,
    Chip,
    Modal,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import ExcelColumnFilter from '../components/ExcelColumnFilter';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
    maxHeight: '90vh',
    overflow: 'auto'
};

const terminalColumns = [
    'merchant_id',
    'serial_number',
    'model',
    'brand',
    'password',
    'name'
];

const requiredTerminalColumns = [
    'serial_number',
    'model',
    'brand',
    'name'
];

const Terminals = () => {
    const [terminals, setTerminals] = useState([]);
    const [merchants, setMerchants] = useState([]);
    const [error, setError] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [columnFilters, setColumnFilters] = useState({});
    const fileInputRef = useRef(null);

    const fetchTerminals = useCallback(async () => {
        try {
            const data = await getTerminals();
            setTerminals(data);
        } catch (err) {
            setError('No se pudieron obtener las terminales.');
        }
    }, []);

    const fetchMerchants = useCallback(async () => {
        try {
            const data = await getMerchants();
            setMerchants(data);
        } catch (err) {
            setError('No se pudieron obtener los comercios.');
        }
    }, []);

    useEffect(() => {
        fetchTerminals();
        fetchMerchants();
    }, [fetchTerminals, fetchMerchants]);

    const handleTerminalCreated = () => {
        setShowCreateForm(false);
        fetchTerminals();
    };

    const downloadTemplate = () => {
        const workbook = XLSX.utils.book_new();
        const templateRows = [
            {
                merchant_id: '',
                serial_number: 'TERM-001',
                model: 'A920',
                brand: 'PAX',
                password: 'Password123',
                name: 'Terminal Mostrador 1'
            }
        ];

        const sheet = XLSX.utils.json_to_sheet(templateRows, { header: terminalColumns });
        XLSX.utils.book_append_sheet(workbook, sheet, 'Terminales');
        XLSX.writeFile(workbook, 'terminals_template.xlsx');
    };

    const getMerchantName = (merchantId) => {
        if (merchantId === null || merchantId === undefined || merchantId === '') {
            return 'terminal no asignada';
        }

        const merchant = merchants.find((item) => item.id === merchantId);
        return merchant?.name || 'terminal no asignada';
    };

    const downloadFilteredReport = () => {
        const workbook = XLSX.utils.book_new();

        const reportColumns = [
            'id',
            'merchant_id',
            'merchant_name',
            'serial_number',
            'model',
            'brand',
            'password',
            'name',
            'status',
            'tip_enabled',
            'msi_enabled',
            'hotel_enabled',
            'qps_enabled',
            'qps_amount',
            'created_at'
        ];

        const reportRows = filteredTerminals.map((terminal) => ({
            id: terminal.id ?? '',
            merchant_id: terminal.merchant_id ?? '',
            merchant_name: getMerchantName(terminal.merchant_id),
            serial_number: terminal.serial_number ?? '',
            model: terminal.model ?? '',
            brand: terminal.brand ?? '',
            password: '',
            name: terminal.name ?? '',
            status: terminal.status ?? '',
            tip_enabled: terminal.tip_enabled ? 'true' : 'false',
            msi_enabled: terminal.msi_enabled ? 'true' : 'false',
            hotel_enabled: terminal.hotel_enabled ? 'true' : 'false',
            qps_enabled: terminal.qps_enabled ? 'true' : 'false',
            qps_amount: terminal.qps_amount ?? 0,
            created_at: terminal.created_at ?? ''
        }));

        const sheet = XLSX.utils.json_to_sheet(reportRows, { header: reportColumns });
        XLSX.utils.book_append_sheet(workbook, sheet, 'ReporteTerminales');
        XLSX.writeFile(workbook, 'terminals_report.xlsx');
    };

    const normalizeRow = (row) => {
        const normalized = {};
        Object.keys(row || {}).forEach((key) => {
            const normalizedKey = key.toString().trim().toLowerCase().replace(/\s+/g, '_');
            normalized[normalizedKey] = row[key];
        });
        return normalized;
    };

    const parseNullableMerchantId = (value) => {
        const text = (value ?? '').toString().trim();
        if (!text) {
            return null;
        }
        const parsed = parseInt(text, 10);
        return Number.isNaN(parsed) ? NaN : parsed;
    };

    const handleBulkUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        setError('');
        setBulkResult(null);
        setBulkLoading(true);

        try {
            const arrayBuffer = await file.arrayBuffer();
            const workbook = XLSX.read(arrayBuffer, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[firstSheetName];

            if (!sheet) {
                throw new Error('No se encontró ninguna hoja en el archivo.');
            }

            const rawRows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
            const rows = rawRows.map(normalizeRow);

            if (rows.length === 0) {
                throw new Error('El archivo está vacío.');
            }

            const missingColumns = requiredTerminalColumns.filter(
                (column) => !Object.keys(rows[0]).includes(column)
            );

            if (missingColumns.length > 0) {
                throw new Error(`Faltan columnas obligatorias: ${missingColumns.join(', ')}`);
            }

            const failures = [];
            let successCount = 0;

            const duplicatedSerialNumbers = new Set();
            const serialCounter = new Map();

            rows.forEach((row) => {
                const serialNumber = (row.serial_number ?? '').toString().trim().toLowerCase();
                if (!serialNumber) {
                    return;
                }
                const count = serialCounter.get(serialNumber) || 0;
                serialCounter.set(serialNumber, count + 1);
                if (count + 1 > 1) {
                    duplicatedSerialNumbers.add(serialNumber);
                }
            });

            for (let index = 0; index < rows.length; index += 1) {
                const row = rows[index];

                const merchantId = parseNullableMerchantId(row.merchant_id);
                const payload = {
                    merchant_id: merchantId,
                    serial_number: (row.serial_number ?? '').toString().trim(),
                    model: (row.model ?? '').toString().trim(),
                    brand: (row.brand ?? '').toString().trim(),
                    password: (row.password ?? '').toString().trim(),
                    name: (row.name ?? '').toString().trim(),
                    status: (row.status ?? '').toString().trim().toLowerCase() || null,
                    allow_update: true
                };

                const hasEmptyRequiredField = requiredTerminalColumns.some((column) => !payload[column]);
                if (hasEmptyRequiredField) {
                    failures.push({
                        row: index + 2,
                        message: 'Uno o más campos obligatorios están vacíos.'
                    });
                    continue;
                }

                if (Number.isNaN(merchantId)) {
                    failures.push({
                        row: index + 2,
                        message: 'merchant_id debe ser numérico o vacío.'
                    });
                    continue;
                }

                if (duplicatedSerialNumbers.has(payload.serial_number.toLowerCase())) {
                    failures.push({
                        row: index + 2,
                        message: 'Número de serie duplicado dentro del archivo.'
                    });
                    continue;
                }

                try {
                    await createTerminal(payload);
                    successCount += 1;
                } catch (requestError) {
                    const responseData = requestError?.response?.data;
                    const errorMessage = typeof responseData === 'string'
                        ? responseData
                        : responseData?.message || 'No se pudo crear o actualizar la terminal.';

                    failures.push({
                        row: index + 2,
                        message: errorMessage
                    });
                }
            }

            setBulkResult({
                total: rows.length,
                successCount,
                failureCount: failures.length,
                failures
            });

            fetchTerminals();
        } catch (uploadError) {
            setError(uploadError.message || 'No se pudo procesar el archivo cargado.');
        } finally {
            setBulkLoading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const updateColumnFilter = (column, values) => {
        setColumnFilters((prev) => ({
            ...prev,
            [column]: values
        }));
    };

    const clearFilters = () => setColumnFilters({});

    const getColumnValue = (terminal, column) => {
        if (column === 'id') {
            return (terminal.id ?? '').toString();
        }
        if (column === 'name') {
            return terminal.name || '';
        }
        if (column === 'serial_number') {
            return terminal.serial_number || '';
        }
        if (column === 'merchant') {
            return getMerchantName(terminal.merchant_id);
        }
        if (column === 'model') {
            return terminal.model || '';
        }
        if (column === 'brand') {
            return terminal.brand || '';
        }
        if (column === 'status') {
            return getStatusLabel(terminal.status);
        }
        return '';
    };

    const matchesColumnFilter = (value, selectedOptions) => {
        if (selectedOptions === undefined) {
            return true;
        }
        return selectedOptions.includes(value);
    };

    const existingSerialNumbers = terminals
        .map((terminal) => (terminal.serial_number || '').trim().toLowerCase())
        .filter((serialNumber) => serialNumber);

    const getStatusLabel = (status) => {
        const normalizedStatus = (status || '').toLowerCase();
        if (normalizedStatus === 'active') {
            return 'Activada';
        }
        if (normalizedStatus === 'blocked') {
            return 'Bloqueada';
        }
        if (normalizedStatus === 'pending') {
            return 'Pendiente';
        }
        return status || '-';
    };

    const getStatusColor = (status) => {
        const normalizedStatus = (status || '').toLowerCase();
        if (normalizedStatus === 'active') {
            return 'success';
        }
        if (normalizedStatus === 'blocked') {
            return 'error';
        }
        if (normalizedStatus === 'pending') {
            return 'warning';
        }
        return 'default';
    };

    const filteredTerminals = terminals.filter((terminal) => {
        return (
            matchesColumnFilter(getColumnValue(terminal, 'id'), columnFilters.id) &&
            matchesColumnFilter(getColumnValue(terminal, 'name'), columnFilters.name) &&
            matchesColumnFilter(getColumnValue(terminal, 'serial_number'), columnFilters.serial_number) &&
            matchesColumnFilter(getColumnValue(terminal, 'merchant'), columnFilters.merchant) &&
            matchesColumnFilter(getColumnValue(terminal, 'model'), columnFilters.model) &&
            matchesColumnFilter(getColumnValue(terminal, 'brand'), columnFilters.brand) &&
            matchesColumnFilter(getColumnValue(terminal, 'status'), columnFilters.status)
        );
    });

    return (
        <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h4" component="h1">Terminales</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadFilteredReport}>
                        Descargar reporte
                    </Button>
                    <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadTemplate}>
                        Descargar plantilla
                    </Button>
                    <Button variant="outlined" component="label" startIcon={<UploadFileIcon />} disabled={bulkLoading}>
                        {bulkLoading ? 'Cargando...' : 'Subir Excel'}
                        <input
                            ref={fileInputRef}
                            type="file"
                            hidden
                            accept=".xlsx,.xls,.csv"
                            onChange={handleBulkUpload}
                        />
                    </Button>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowCreateForm(true)}>
                        Agregar terminal
                    </Button>
                    <Button variant="text" onClick={clearFilters}>Limpiar filtros</Button>
                </Box>
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                    Usa la plantilla para carga masiva. Campos obligatorios: serial_number, model, brand y name.
                </Typography>
                <Typography variant="body2">
                    Campos opcionales: merchant_id, status y password.
                </Typography>
            </Alert>

            {error && <Typography color="error">{error}</Typography>}
            {bulkResult && (
                <Alert severity={bulkResult.failureCount > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
                    Se procesaron {bulkResult.total} filas: {bulkResult.successCount} creadas, {bulkResult.failureCount} con error.
                    {bulkResult.failureCount > 0 && (
                        <Box sx={{ mt: 1 }}>
                            {bulkResult.failures.slice(0, 5).map((failure) => (
                                <Typography key={`${failure.row}-${failure.message}`} variant="body2">
                                    Fila {failure.row}: {failure.message}
                                </Typography>
                            ))}
                        </Box>
                    )}
                </Alert>
            )}

            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <ExcelColumnFilter
                                    label="ID"
                                    options={terminals.map((item) => getColumnValue(item, 'id'))}
                                    selectedOptions={columnFilters.id}
                                    onChange={(values) => updateColumnFilter('id', values)}
                                />
                            </TableCell>
                            <TableCell>
                                <ExcelColumnFilter
                                    label="Nombre"
                                    options={terminals.map((item) => getColumnValue(item, 'name'))}
                                    selectedOptions={columnFilters.name}
                                    onChange={(values) => updateColumnFilter('name', values)}
                                />
                            </TableCell>
                            <TableCell>
                                <ExcelColumnFilter
                                    label="Número de serie"
                                    options={terminals.map((item) => getColumnValue(item, 'serial_number'))}
                                    selectedOptions={columnFilters.serial_number}
                                    onChange={(values) => updateColumnFilter('serial_number', values)}
                                />
                            </TableCell>
                            <TableCell>
                                <ExcelColumnFilter
                                    label="Comercio"
                                    options={terminals.map((item) => getColumnValue(item, 'merchant'))}
                                    selectedOptions={columnFilters.merchant}
                                    onChange={(values) => updateColumnFilter('merchant', values)}
                                />
                            </TableCell>
                            <TableCell>
                                <ExcelColumnFilter
                                    label="Modelo"
                                    options={terminals.map((item) => getColumnValue(item, 'model'))}
                                    selectedOptions={columnFilters.model}
                                    onChange={(values) => updateColumnFilter('model', values)}
                                />
                            </TableCell>
                            <TableCell>
                                <ExcelColumnFilter
                                    label="Marca"
                                    options={terminals.map((item) => getColumnValue(item, 'brand'))}
                                    selectedOptions={columnFilters.brand}
                                    onChange={(values) => updateColumnFilter('brand', values)}
                                />
                            </TableCell>
                            <TableCell>
                                <ExcelColumnFilter
                                    label="Estado"
                                    options={terminals.map((item) => getColumnValue(item, 'status'))}
                                    selectedOptions={columnFilters.status}
                                    onChange={(values) => updateColumnFilter('status', values)}
                                />
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredTerminals.map((terminal) => (
                            <TableRow key={terminal.id}>
                                <TableCell>{terminal.id}</TableCell>
                                <TableCell>{terminal.name}</TableCell>
                                <TableCell>{terminal.serial_number}</TableCell>
                                <TableCell>{getMerchantName(terminal.merchant_id)}</TableCell>
                                <TableCell>{terminal.model}</TableCell>
                                <TableCell>{terminal.brand}</TableCell>
                                <TableCell>
                                    <Chip size="small" label={getStatusLabel(terminal.status)} color={getStatusColor(terminal.status)} />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Modal open={showCreateForm} onClose={() => setShowCreateForm(false)}>
                <Box sx={style}>
                    <CreateTerminalForm
                        onTerminalCreated={handleTerminalCreated}
                        createTerminal={createTerminal}
                        merchants={merchants}
                        existingSerialNumbers={existingSerialNumbers}
                    />
                </Box>
            </Modal>
        </Paper>
    );
};

export default Terminals;
